import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { adminSymbols } from '@/api/admin';
import { ApiError } from '@/lib/http';
import { toast } from '@/components/ui/sonner';

interface Props {
  symbolId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * 紧急覆盖弹窗 — §6.3.6 + openapi.yaml ApplyPendingNowRequest:
 * 立即把 pending 应用,需要 reason(≥4 字)。绕过次日 03:05 生效机制。
 */
export function ApplyPendingNowDialog({ symbolId, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
    }
  }, [open]);

  const submit = useMutation({
    mutationFn: () => adminSymbols.applyPendingNow(symbolId!, { reason: reason.trim() }),
    onSuccess: () => {
      toast.success('已立即生效', { description: '本品种 pending 参数已应用,跟单引擎按新参数运行' });
      qc.invalidateQueries({ queryKey: ['admin', 'symbols'] });
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error('立即应用失败', { description: err instanceof ApiError ? err.message : '未知错误' });
    },
  });

  const reasonOk = reason.trim().length >= 4;
  const canSubmit = reasonOk && !submit.isPending && !!symbolId;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-warn">
            <ShieldAlert className="h-5 w-5" />
            紧急覆盖 · 立即应用 pending
          </DialogTitle>
          <DialogDescription>
            正常修改会在次日 03:05 生效,本操作绕过该机制立即生效。
            建议仅在风险事件后需要立即调整跟单参数时使用。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="apn-reason">操作原因(至少 4 字)</Label>
            <Input
              id="apn-reason"
              placeholder="例如:风控告警后需立即降比例"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submit.isPending}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submit.isPending}>
            取消
          </Button>
          <Button variant="warn" onClick={() => submit.mutate()} disabled={!canSubmit}>
            {submit.isPending ? '应用中...' : '立即应用'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
